<template>
  <v-card
    elevation="0"
    outlined
  >
    <v-card-title>
      Specify the conditions that leads must meet.
      If no conditions are defined, all leads will match by default.
    </v-card-title>
    <v-card-text />
    <v-table fixed-header>
      <thead>
        <tr>
          <th>Lead Column</th>
          <th>Comparison Operators</th>
          <th>Values</th>
          <th />
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(condition, i) in proxyValue"
          :key="i"
        >
          <td>
            <v-select
              v-model="proxyValue[i].leadColumn"
              dense
              :items="leadColumns"
              @update:model-value="handleLeadColumnChange(i)"
            />
          </td>

          <td>
            <v-select
              v-model="proxyValue[i].comparisonOperator"
              dense
              :items="getInputConfig(proxyValue[i].leadColumn).availableOperators"
              item-title="text"
              item-value="value"
            />
          </td>

          <td style="min-width: 420px">
            <component
              :is="getInputConfig(proxyValue[i].leadColumn).component"
              v-model="proxyValue[i].targetValue"
              v-bind="getInputConfig(proxyValue[i].leadColumn).attributes"
              required
              dense
              :error-messages="getFieldError('targetValue', i)"
            />
          </td>
          <td class="text-right">
            <v-btn
              color="error"
              icon
              @click="removeCondition(i)"
            >
              <v-icon> mdi-trash-can-outline </v-icon>
            </v-btn>
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <td
            colspan="4"
            class="text-right"
          >
            <v-btn
              color="primary"
              small
              @click="addRow"
            >
              <v-icon> mdi-plus </v-icon> Add Condition
            </v-btn>
          </td>
        </tr>
      </tfoot>
    </v-table>
  </v-card>
</template>

<script setup>
import { mapActions } from 'vuex';
import { VTextField, VSelect } from 'vuetify/components';
</script>

<script>
export default {
  name: 'AbTestConditionList',

  props: {
    leadColumns: {
      type: Array,
      default: () => [],
    },

    leadColumInputDefs: {
      type: Object,
      default: () => ({}),
    },

    comparisonOperators: {
      type: Array,
      default: () => [],
    },

    modelValue: {
      type: Array,
      default: () => [],
    },

    serverErrors: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['update:modelValue'],

  computed: {
    proxyValue() {
      return this.modelValue;
    },
  },

  methods: {
    ...mapActions('notification', ['setNotification']),

    // Builds input config based on lead column definition
    getInputConfig(leadColumn) {
      const defaultConfig = {
        component: VTextField,
        attributes: { type: 'text' },
        availableOperators: this.comparisonOperators,
      };

      const definition = this.leadColumInputDefs[leadColumn];
      if (!definition) return defaultConfig;

      // Define handlers for different input types
      const typeHandlers = {
        select: () => {
          defaultConfig.component = VSelect;

          const items = Object.entries(definition.options)
            .map(([value, text]) => ({ value, text }));

          defaultConfig.attributes = {
            items,
            itemTitle: 'text',
            itemValue: 'value',
            multiple: definition.multiple || false,
            chips: definition.multiple || false,
          };
        },
        number: () => {
          defaultConfig.attributes = {
            type: 'number',
            min: definition.min || undefined,
            max: definition.max || undefined,
          };
        },
        date: () => {
          defaultConfig.attributes = {
            type: 'date',
          };
        },
      };

      // Apply handler if the type is supported
      const applyTypeHandler = typeHandlers[definition.type];
      if (applyTypeHandler) applyTypeHandler();

      // Apply allowed operators if specified
      if (definition.operators) {
        defaultConfig.availableOperators = this.comparisonOperators
          .filter((operator) => definition.operators.includes(operator.value));
      }

      return defaultConfig;
    },

    addRow() {
      this.$emit('update:modelValue', [
        ...(this.modelValue || []),
        {
          leadColumn: 'campaign',
          comparisonOperator: '==',
          targetValue: '',
        },
      ]);
    },

    removeCondition(index) {
      this.$emit(
        'update:modelValue',
        (this.modelValue || []).filter((_, idx) => idx !== index),
      );
    },

    handleLeadColumnChange(index) {
      const selectedLeadColumn = this.proxyValue[index].leadColumn;
      const inputConfig = this.getInputConfig(selectedLeadColumn);

      // Update the comparison operator and target value based on the new lead column
      this.proxyValue[index].comparisonOperator = inputConfig.availableOperators[0]?.value || '==';
      this.proxyValue[index].targetValue = '';
    },

    getFieldError(fieldName, index) {
      const serverErrorKey = `selection_conditions.${index}.${fieldName}`;

      if (this.serverErrors && this.serverErrors[serverErrorKey]) {
        return this.serverErrors[serverErrorKey];
      }

      return [];
    },
  },
};
</script>
